import { TestLocators } from 'constants/Locators';
import { RequestStatus } from 'constants/RequestStatus';

import { Loader, MUI, ThemeMode } from 'ci-esi-web-shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { actions as privateRouteActions } from 'app/containers/PrivateRoute/slice';
import { colors } from 'styles/theme/colors';
import { ProductUpdateType } from 'types/ProductUpdate';

import { LOADER_SIZE, NOTIFICATIONS_LIST_LENGTH } from './constants';
import { NotificationsList } from './NotificationsList';
import { ParsedProductUpdate } from './types';

interface NotificationBodyProps {
  status: RequestStatus;
  notifications: ParsedProductUpdate[];
  showMarkAllAsRead: boolean;
  handleAllAsRead: () => void;
  handleSelectNotificationsCategory: (category: ProductUpdateType) => void;
}

export function NotificationBody({
  status,
  notifications,
  showMarkAllAsRead,
  handleAllAsRead,
  handleSelectNotificationsCategory,
}: NotificationBodyProps) {
  const dispatch = useDispatch();
  const [notificationsData, setNotificationsData] = useState<ParsedProductUpdate[]>();
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;

  const menuItemSx = { width: 365 };
  const headerSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 20px',
    backgroundColor: isDarkMode ? colors.grey40 : colors.primaryWhite,
  };
  const labelSx = { color: isDarkMode ? colors.baseGrey10 : colors.grey42 };
  const markAllSx = {
    cursor: 'pointer',
    fontSize: 12,
    color: isDarkMode ? colors.primaryWhite : colors.primary,
    textDecoration: 'none',
    fontWeight: 400,
  };
  const animatedBorderSx = {
    background: colors.grey45,
    width: '100%',
    height: 1,
  };
  const containerSx = {
    position: 'relative',
    width: '365px',
    padding: '20px',
    backgroundColor: isDarkMode ? colors.grey40 : colors.primaryWhite,
    color: colors.primaryWhite,
    marginBottom: 'auto',
    overflowY: 'auto',
  };
  const loaderSx = { minHeight: '500px' };
  const viewAllContainerSx = {
    textAlign: 'center',
    padding: '10px 0',
    backgroundColor: isDarkMode ? colors.grey40 : colors.primaryWhite,
  };
  const viewAllLinkSx = {
    outline: 'none',
    background: 'none',
    border: 0,
    cursor: 'pointer',
    color: isDarkMode ? colors.primaryWhite : colors.primary,
    textDecoration: 'none',
    fontWeight: 400,
  };
  const notificationGroupsContainerSx = {
    '& .MuiButtonGroup-root': { width: '100%' },
  };
  const groupButtonSx = {
    color: isDarkMode ? 'inherit' : colors.primary,
    width: '100%',
    borderColor: isDarkMode ? colors.grey45 : 'rgba(0, 137, 209, 0.23)',
    backgroundColor: isDarkMode ? colors.grey40 : colors.primaryWhite,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    '&:hover': {
      backgroundColor: isDarkMode ? colors.grey44 : colors.primaryWhite,
      borderColor: isDarkMode ? colors.grey45 : 'rgba(0, 137, 209, 0.23)',
    },
  };

  useEffect(() => {
    dispatch(privateRouteActions.updateUserVisitedDates());
  }, [dispatch]);

  useEffect(() => {
    setNotificationsData(notifications);
  }, [notifications]);

  const filteredNotifications = useMemo(
    () => notificationsData?.filter(({ productupdatetype }) => productupdatetype === ProductUpdateType.Notification),
    [notificationsData],
  );

  const renderNotificationsList = useMemo(
    () => (
      <NotificationsList
        notifications={filteredNotifications?.slice(0, NOTIFICATIONS_LIST_LENGTH)}
        handleSelectNotificationsCategory={handleSelectNotificationsCategory}
      />
    ),
    [filteredNotifications, handleSelectNotificationsCategory],
  );

  return (
    <>
      <MUI.Box sx={menuItemSx}>
        <MUI.Box sx={headerSx}>
          <MUI.Typography variant="body1" sx={labelSx}>
            {t('LANDING_PAGE.NOTIFICATIONS.TITLE')}
          </MUI.Typography>
          {showMarkAllAsRead && (
            <MUI.Typography
              variant="h5"
              onClick={handleAllAsRead}
              sx={markAllSx}
              data-testid={TestLocators.MarkAllRead}
            >
              {t('LANDING_PAGE.NOTIFICATIONS.MARK_ALL')}
            </MUI.Typography>
          )}
        </MUI.Box>
        <div style={animatedBorderSx} />
      </MUI.Box>
      <MUI.Box data-testid={TestLocators.Landing_Notifications_Content} sx={containerSx}>
        {status === RequestStatus.LOADING && (
          <MUI.Box sx={loaderSx}>
            <Loader size={LOADER_SIZE} />
          </MUI.Box>
        )}
        {renderNotificationsList}
      </MUI.Box>
      {filteredNotifications && (
        <MUI.Box sx={viewAllContainerSx}>
          <MUI.Box
            component="button"
            data-testid={TestLocators.Notifications_AllNotifications}
            onClick={() => handleSelectNotificationsCategory(ProductUpdateType.Notification)}
            sx={viewAllLinkSx}
          >
            {t('LANDING_PAGE.NOTIFICATIONS.VIEW_ALL')}
          </MUI.Box>
        </MUI.Box>
      )}
      <MUI.Box sx={notificationGroupsContainerSx}>
        <MUI.ButtonGroup>
          <MUI.Button
            data-testid={TestLocators.Notifications_DataUpdates}
            sx={groupButtonSx}
            onClick={() => handleSelectNotificationsCategory(ProductUpdateType.DataUpdate)}
          >
            {t('LANDING_PAGE.DATA_UPDATES')}
          </MUI.Button>
          <MUI.Button
            data-testid={TestLocators.Notifications_PlatformUpdates}
            sx={groupButtonSx}
            onClick={() => handleSelectNotificationsCategory(ProductUpdateType.ProductUpdate)}
          >
            {t('LANDING_PAGE.PLATFORM_UPDATES')}
          </MUI.Button>
        </MUI.ButtonGroup>
      </MUI.Box>
    </>
  );
}
